import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

function Contact() {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        // Force scroll to top and ensure it's applied
        window.scrollTo(0, 0);

        // Add a small delay to ensure the scroll is applied
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            setIsLoaded(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Phone number formatting
        if (name === 'phone' && value) {
            // Remove all non-digit characters except +
            const cleaned = value.replace(/[^\d+]/g, '');
            // Ensure it starts with +64 for NZ numbers if it doesn't start with +
            let formatted = cleaned;
            if (!cleaned.startsWith('+') && cleaned.startsWith('64')) {
                formatted = '+' + cleaned;
            } else if (!cleaned.startsWith('+') && !cleaned.startsWith('64')) {
                formatted = '+64' + cleaned;
            }

            setFormData({
                ...formData,
                [name]: formatted
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const validateForm = () => {
        if (!formData.name.trim()) {
            setSubmitStatus('error');
            return false;
        }
        if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
            setSubmitStatus('error');
            return false;
        }
        if (!formData.message.trim()) {
            setSubmitStatus('error');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch('https://t4pkransz3.execute-api.ap-southeast-2.amazonaws.com/prod/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': process.env.REACT_APP_API_KEY || 'your-api-key-here'
                },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    phone: formData.phone.trim() || undefined,
                    message: formData.message.trim()
                })
            });

            const result = await response.json();

            if (response.ok) {
                setSubmitStatus('success');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: ''
                });
            } else {
                setSubmitStatus('error');
                console.error('API Error:', result);
            }
        } catch (error) {
            setSubmitStatus('error');
            console.error('Network Error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isLoaded) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto mb-4"></div>
                    <p className="text-brand-green">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ opacity: 1 }}>
            {/* Contact Form & Info */}
            <section className="py-32 bg-brand-cream">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-24">
                        {/* Contact Form - Takes up 3/5 of the space */}
                        <motion.div
                            className="lg:col-span-3"
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                        >
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                >
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Your Name*
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                                        placeholder="Your full name"
                                    />
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.4 }}
                                >
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Your Email*
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                                        placeholder="your.email@example.com"
                                    />
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.5 }}
                                >
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                                        Your Phone Number <span className="text-gray-500">(optional)</span>
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                                        placeholder="+64 21 123 4567"
                                    />
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                >
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                        Your Message
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows={6}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors resize-none"
                                        placeholder="Tell us about your project or inquiry..."
                                    ></textarea>
                                </motion.div>

                                <motion.button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${isSubmitting
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-brand-orange hover:bg-brand-orange-hover text-white'
                                        }`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.7 }}
                                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </motion.button>

                                {/* Status Messages */}
                                {submitStatus === 'success' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="p-4 bg-green-50 border border-green-200 rounded-lg"
                                    >
                                        <p className="text-green-800 text-center">
                                            ✅ Thank you for your message! We will get back to you soon.
                                        </p>
                                    </motion.div>
                                )}

                                {submitStatus === 'error' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="p-4 bg-red-50 border border-red-200 rounded-lg"
                                    >
                                        <p className="text-red-800 text-center">
                                            ❌ Please check that all required fields are filled correctly.
                                        </p>
                                    </motion.div>
                                )}
                            </form>
                        </motion.div>

                        {/* Contact Information - Takes up 2/5 of the space */}
                        <motion.div
                            className="lg:col-span-2 flex items-center"
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                        >
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-8">Reach out with any questions you have</h2>

                                <div className="space-y-6">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Call us directly</p>
                                        <p className="text-xl font-bold text-gray-900">021-520-201  /  021-229-8869</p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Contact email</p>
                                        <p className="text-xl font-bold text-gray-900">operation@meganz.co.nz</p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Visit us</p>
                                        <p className="text-lg font-semibold text-gray-900">6b Puriri Street,<br />New Lynn, Auckland</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Full Width Google Map */}
            <section className="w-full">
                <div className="h-[600px]">
                    <iframe
                        src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=6b+Puriri+Street,+New+Lynn,+Auckland,+New+Zealand`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Mega Property Solutions Location"
                    ></iframe>
                </div>
            </section>

        </div>
    );
}

export default Contact;
